/**
 * Log Integrity and Tampering Prevention Utilities
 * Provides basic integrity checking for audit logs
 */

/**
 * Generate a hash for a log entry to detect tampering
 * @param {Object} logEntry - The log entry object
 * @returns {string} SHA-256 hash of the log entry
 */
export const generateLogHash = (logEntry) => {
  // Create a normalized string representation of the log entry
  const normalizedEntry = {
    timestamp: logEntry.timestamp?.toISOString?.() || logEntry.timestamp,
    userId: logEntry.userId,
    action: logEntry.action,
    resource: logEntry.resource,
    resourceId: logEntry.resourceId,
    changes: logEntry.changes,
    metadata: {
      ipAddress: logEntry.metadata?.ipAddress,
      userAgent: logEntry.metadata?.userAgent,
      sessionId: logEntry.metadata?.sessionId
    }
  };

  // Sort keys to ensure consistent hashing
  const sortedString = JSON.stringify(normalizedEntry, Object.keys(normalizedEntry).sort());
  
  // In a browser environment, we'll use a simple hash function
  // In production, you'd want to use a proper crypto library
  return simpleHash(sortedString);
};

/**
 * Simple hash function for browser environments
 * Note: This is not cryptographically secure - use crypto.subtle in production
 * @param {string} str - String to hash
 * @returns {string} Hash string
 */
const simpleHash = (str) => {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(16);
};

/**
 * Verify the integrity of a log entry
 * @param {Object} logEntry - The log entry to verify
 * @param {string} expectedHash - The expected hash value
 * @returns {boolean} True if the log entry is valid
 */
export const verifyLogIntegrity = (logEntry, expectedHash) => {
  const computedHash = generateLogHash(logEntry);
  return computedHash === expectedHash;
};

/**
 * Create a tamper-evident log entry
 * @param {Object} logData - The original log data
 * @returns {Object} Log entry with integrity hash
 */
export const createTamperEvidentLog = (logData) => {
  const hash = generateLogHash(logData);
  
  return {
    ...logData,
    integrity: {
      hash,
      algorithm: 'simple-hash',
      timestamp: new Date().toISOString()
    }
  };
};

/**
 * Validate a collection of logs for tampering
 * @param {Array} logs - Array of log entries
 * @returns {Object} Validation results
 */
export const validateLogChain = (logs) => {
  const results = {
    valid: true,
    errors: [],
    warnings: [],
    totalLogs: logs.length,
    validLogs: 0,
    invalidLogs: 0
  };

  logs.forEach((log, index) => {
    if (!log.integrity) {
      results.warnings.push({
        index,
        logId: log.id,
        message: 'Log entry missing integrity information',
        severity: 'warning'
      });
      return;
    }

    const isValid = verifyLogIntegrity(log, log.integrity.hash);
    
    if (isValid) {
      results.validLogs++;
    } else {
      results.invalidLogs++;
      results.valid = false;
      results.errors.push({
        index,
        logId: log.id,
        message: 'Log entry failed integrity check - possible tampering detected',
        severity: 'error',
        expected: log.integrity.hash,
        computed: generateLogHash(log)
      });
    }
  });

  return results;
};

/**
 * Create a secure log summary for monitoring
 * @param {Array} logs - Array of log entries
 * @returns {Object} Security summary
 */
export const createSecuritySummary = (logs) => {
  const summary = {
    totalLogs: logs.length,
    timeRange: {
      start: null,
      end: null
    },
    actionTypes: {},
    userActivity: {},
    integrityStatus: 'unknown',
    suspiciousActivity: []
  };

  if (logs.length === 0) return summary;

  // Sort logs by timestamp to get time range
  const sortedLogs = [...logs].sort((a, b) => {
    const aTime = new Date(a.timestamp?.toDate?.() || a.timestamp);
    const bTime = new Date(b.timestamp?.toDate?.() || b.timestamp);
    return aTime - bTime;
  });

  summary.timeRange.start = sortedLogs[0].timestamp;
  summary.timeRange.end = sortedLogs[sortedLogs.length - 1].timestamp;

  // Analyze logs
  logs.forEach(log => {
    // Count action types
    summary.actionTypes[log.action] = (summary.actionTypes[log.action] || 0) + 1;
    
    // Count user activity
    summary.userActivity[log.userId] = (summary.userActivity[log.userId] || 0) + 1;
    
    // Detect suspicious patterns
    if (log.action === 'DELETE' && log.severity === 'HIGH') {
      summary.suspiciousActivity.push({
        type: 'high_risk_deletion',
        logId: log.id,
        userId: log.userId,
        timestamp: log.timestamp,
        details: `High-severity deletion by user ${log.userId}`
      });
    }
    
    // Check for unusual bulk operations
    const userLogs = logs.filter(l => l.userId === log.userId);
    if (userLogs.length > 50) { // Threshold for bulk operations
      const bulkActivity = summary.suspiciousActivity.find(
        s => s.type === 'bulk_operations' && s.userId === log.userId
      );
      if (!bulkActivity) {
        summary.suspiciousActivity.push({
          type: 'bulk_operations',
          userId: log.userId,
          count: userLogs.length,
          details: `User performed ${userLogs.length} operations`
        });
      }
    }
  });

  // Check overall integrity
  const validation = validateLogChain(logs);
  summary.integrityStatus = validation.valid ? 'valid' : 'compromised';
  summary.integrityDetails = validation;

  return summary;
};

const logSecurityUtils = {
  generateLogHash,
  verifyLogIntegrity,
  createTamperEvidentLog,
  validateLogChain,
  createSecuritySummary
};
export default logSecurityUtils;
